import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessagesSquare, PenSquare, Pin, Pencil } from "lucide-react";

export default function Forum() {
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/content/forum_rules")]).then(([cats, r]) => {
      setCategories(cats.data);
      setRules(r.data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="k-container py-12 md:py-16">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="font-display text-6xl text-ink-900" data-testid="forum-title">
            Tartışmalar
          </h1>
          <p className="mt-2 font-body text-ink-700">
            Konu bazlı dört küçük alan — güvenli, yavaş ve samimi.
          </p>
        </div>
        {user && (
          <div className="flex gap-2">
            {user.role === "admin" && (
              <Link to="/admin/kategoriler" className="k-btn-secondary" data-testid="admin-categories-link">
                Kategorileri düzenle
              </Link>
            )}
            <Link to="/topic-new" className="k-btn-primary" data-testid="forum-new-topic-btn">
              <PenSquare className="mr-2 h-4 w-4" /> Yeni konu aç
            </Link>
          </div>
        )}
      </div>

      <div className="mt-10 grid gap-5 md:grid-cols-2">
        {loading && [1, 2, 3, 4].map((i) => <div key={i} className="k-card h-40 animate-pulse p-8" />)}
        {!loading && rules && <PinnedRulesCard rules={rules} isAdmin={user?.role === "admin"} />}
        {!loading &&
          categories.map((c) => (
            <Link
              key={c.slug}
              to={`/forum/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="k-card group p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm"
            >
              <div className="flex items-start justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-earth-400/20 text-earth-500">
                  <MessagesSquare className="h-6 w-6" />
                </span>
                <span className="k-tag">{c.topic_count} konu</span>
              </div>
              <h2 className="mt-5 font-heading text-2xl font-bold text-ink-900 group-hover:text-sage-700 transition-colors">
                {c.name}
              </h2>
              <p className="mt-2 font-body text-ink-700">{c.description}</p>
            </Link>
          ))}
      </div>
    </div>
  );
}

function PinnedRulesCard({ rules, isAdmin }) {
  return (
    <article
      className="md:col-span-2 relative rounded-3xl border-2 border-sage-500/40 bg-sage-500/10 p-8 shadow-soft"
      data-testid="pinned-rules-card"
    >
      <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-sage-500 px-3 py-1 text-xs font-bold uppercase tracking-widest text-cream-50">
        <Pin className="h-3.5 w-3.5" /> Sabitlenmiş
      </div>
      {isAdmin && (
        <Link
          to="/admin/kurallar"
          className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-cream-50/80 px-3 py-1 text-xs font-semibold text-sage-700 hover:bg-cream-50"
          data-testid="edit-rules-btn"
        >
          <Pencil className="h-3.5 w-3.5" /> Düzenle
        </Link>
      )}
      <div className="flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-sage-500 text-cream-50">
          <Pin className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-2xl font-bold text-ink-900" data-testid="pinned-rules-title">
            {rules.title}
          </h2>
          <div
            className="k-prose mt-3 !text-base"
            data-testid="pinned-rules-body"
            dangerouslySetInnerHTML={{ __html: rules.body }}
          />
        </div>
      </div>
    </article>
  );
}
